import React from 'react';
import { Button, EmptyState, Skeleton, Select, rangeLabelFor } from '@parsina/framework';
import { EncountersMobileListSkeleton } from './EncountersSkeleton';
import { CalendarX2, SearchX, RotateCw } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useEncounters } from '../../hooks/useEncounters';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import MobileFilterBar, { FilterField } from '../../components/mobile/MobileFilterBar';
import MobileEncounterCard from '../../components/mobile/MobileEncounterCard';
import {
  DEFAULTS, FILTERS, TYPE_OPTIONS, PAGE_SIZE, datePresetsFor,
  useEncounterFilters, useClinicOptions, useEncounterSearch,
} from './Encounters.shared';

// ─────────────────────────────────────────────────────────────────────────────
// سوابق ویزیت‌ها — the mobile encounter history (§B.2, S11).
//
// The screen that did not exist. `/encounters` had a desktop table, a PWA
// shortcut pointing straight at it, and nothing else — so a doctor who tapped
// «سوابق ویزیت‌ها» on their home screen got a six-column table on a 393px
// phone. With no mobile Encounters and no patient lookup, a phone could open
// exactly one thing: today's queue.
//
// An independent render tree over `Encounters.shared` — the same vocabularies,
// the same URL contract and the same query window the desktop table uses
// (§1.1). What it drops is the view toggle: on a phone there are cards and
// nothing to toggle to.
//
// The list ACCUMULATES through «نمایش بیشتر» rather than paging in place
// (§4: no infinite scroll, and no pager on a phone either — the desktop's
// scroll-to-load, PM-0533, is its own exception). Like the desktop it lists
// PEOPLE, one row each, over the month by default.
// ─────────────────────────────────────────────────────────────────────────────

const EncountersMobile = () => {
  const i18n = useTranslation();
  const { t, formatNumber } = i18n;
  const navigate = useAppNavigate();

  const {
    setParams, filter, type, clinicId, query, preset, custom,
    fromTs, toTs, hasActiveFilters, clearFilters,
  } = useEncounterFilters();
  const { clinics, clinicNameById } = useClinicOptions();
  const { search, onSearch } = useEncounterSearch(query, setParams);

  // Accumulate rather than replace: «نمایش بیشتر» must add to what is on
  // screen, not swap it for the next page and leave no way back.
  const [pages, setPages] = React.useState(1);
  React.useEffect(() => {
    setPages(1);
  }, [filter, type, clinicId, query, preset, custom.from, custom.to]);

  const { encounters, total, loading, error, reload } = useEncounters(
    filter !== 'all' ? filter : null, query, clinicId, type,
    { fromTs, toTs, limit: PAGE_SIZE * pages, offset: 0, onePerPatient: true },
  );

  const rangeLabel = rangeLabelFor(preset, custom.from, custom.to, datePresetsFor(t));
  const hiddenCount = Math.max(0, total - encounters.length);

  const isEmpty = !loading && !error && encounters.length === 0;
  const dataState = loading ? 'loading' : error ? 'error' : isEmpty ? 'empty' : 'content';
  // Stale-while-refetch (§2.4/§8): a refetch keeps the previous list visible
  // and dimmed. Skeletons are for the first load only — a list that blanks on
  // every filter tap reads as a broken screen.
  const stale = loading && encounters.length > 0;
  const skeleton = loading && encounters.length === 0;

  // How many non-default filters are on, for the «فیلترها» badge. The status
  // chips are inline, so they are not counted here.
  const sheetFilterCount = [type, clinicId].filter(Boolean).length
    + (preset !== DEFAULTS.preset ? 1 : 0);

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>

      {/* A top-level destination: the title, no back affordance — the bottom
          nav is the way out (§C.2, list destination). */}
      <div style={{ flexShrink: 0 }}>
        {/* Compact (PM-0120, PM-0116): the gradient band with a subtitle took
            340 of 2340 px before the search; the title and «به‌روزرسانی» fit one row. */}
        <MobileHeader
          compact
          title={t('encounters.title')}
          actions={[{
            key: 'reload',
            Icon: RotateCw,
            label: t('common.actions.refresh'),
            onClick: reload,
          }]}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '1rem' }}>

        {/* The bar rides the header's bottom edge, like the accounting one. */}
        <div style={{ padding: '0.875rem 0.875rem 0' }}>
          <MobileFilterBar
            testId="encounters-toolbar"
            search={search}
            onSearch={onSearch}
            searchPlaceholder={t('encounters.searchPlaceholder')}
            chips={FILTERS.map(f => ({ id: f.id, label: t(f.labelKey) }))}
            chipValue={filter}
            onChip={(id) => setParams({ status: id })}
            chipsLabel={t('encounters.statusFilter')}
            presets={datePresetsFor(t)}
            preset={preset}
            custom={custom}
            onPreset={(id) => setParams({ preset: id, from: '', to: '' })}
            onCustom={(from, to) => setParams({ preset: 'custom', from, to })}
            rangeLabel={rangeLabel}
            activeCount={sheetFilterCount}
            onClear={clearFilters}
            clearLabel={t('encounters.clearFilters')}
            extraFilters={(
              <>
                <FilterField label={t('encounters.columns.type')} testId="encounters-filter-type">
                  <Select
                    value={type}
                    onChange={(e) => setParams({ type: e.target.value })}
                    options={TYPE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey) }))}
                    placeholder={t('encounters.allTypes')}
                    aria-label={t('encounters.columns.type')}
                  />
                </FilterField>

                {clinics.length > 0 && (
                  <FilterField label={t('common.labels.clinic')} testId="encounters-filter-clinic">
                    <Select
                      value={clinicId}
                      onChange={(e) => setParams({ clinic: e.target.value })}
                      options={clinics.map(c => ({ value: c.id, label: c.name }))}
                      placeholder={t('encounters.allClinics')}
                      aria-label={t('common.labels.clinic')}
                    />
                  </FilterField>
                )}
              </>
            )}
          />
        </div>

        {error ? (
          <div data-testid="encounters-content" data-state="error" style={{ padding: '0.875rem' }}>
            <ErrorState error={error} title={t('encounters.unavailable')} onRetry={reload} />
          </div>
        ) : (
          <div data-testid="encounters-content" data-state={dataState} aria-busy={loading || undefined}>

            {/* The count, above the list — the desktop toolbar's «۲۴ مورد». */}
            <div style={{ padding: '0.875rem 0.875rem 0.5rem', fontSize: '0.75rem', color: C.textMuted }}>
              {skeleton ? <Skeleton width={72} height={11} /> : t('patient.lookup.count', { count: total })}
            </div>

            <div
              style={{
                padding: '0 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem',
                opacity: stale ? 0.45 : 1,
                pointerEvents: stale ? 'none' : undefined,
                transition: 'opacity 0.2s',
              }}
            >
              {skeleton && <EncountersMobileListSkeleton count={4} />}

              {/* Filtered-empty is not the same claim as truly-empty (§2.4).
                  The default view is scoped to «این ماه», so the unfiltered copy
                  makes no claim about a particular day. */}
              {isEmpty && (hasActiveFilters ? (
                <EmptyState
                  icon={<SearchX size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
                  title={t('errors.empty.filters')}
                  description={t('encounters.empty.filtersHint')}
                  action={(
                    <Button variant="outline" size="sm" className="min-h-11" onClick={clearFilters} data-testid="encounters-empty-clear">
                      {t('encounters.clearFilters')}
                    </Button>
                  )}
                />
              ) : (
                <EmptyState
                  icon={<CalendarX2 size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
                  title={t('visit.empty.noVisits')}
                  description={t('encounters.empty.todayHint')}
                />
              ))}

              {encounters.map(e => (
                <MobileEncounterCard
                  key={e.id}
                  encounter={e}
                  clinicName={clinicNameById[e.clinicId]}
                  onOpen={(enc) => navigate('patient-detail', {
                    patientId: enc.patientId, id: enc.patientId, tab: 'visits',
                  })}
                />
              ))}

              {hiddenCount > 0 && !skeleton && (
                <Button
                  variant="outline"
                  data-testid="encounters-more"
                  className="w-full min-h-11"
                  disabled={loading}
                  onClick={() => setPages(p => p + 1)}
                >
                  {t('dashboard.queue.showMore', { count: formatNumber(hiddenCount) })}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EncountersMobile;
